/**
 * First-party UTM capture.
 *
 * On every page load (and client-side navigation) we look for utm_source,
 * utm_medium, utm_campaign, utm_content and utm_term in the URL. If any are
 * present we store them - plus the full landing page URL and a timestamp - in
 * the `om_utm` cookie + localStorage for 90 days.
 *
 * Two copies are kept:
 *   - first touch (`om_utm_first`): written once, never overwritten
 *   - last touch  (`om_utm`): overwritten by each new tagged visit
 *
 * The cookies are deliberately first-party and server-readable, so every
 * signup endpoint can stamp attribution onto the record it creates.
 */

export const UTM_COOKIE = 'om_utm';
export const UTM_FIRST_COOKIE = 'om_utm_first';
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const MAX_AGE_DAYS = 90;

const clean = (v) => (typeof v === 'string' ? v.trim().slice(0, 300) : '');

const readStore = (key) => {
  try {
    const hit = document.cookie.split('; ').find((row) => row.startsWith(`${key}=`));
    if (hit) return JSON.parse(decodeURIComponent(hit.split('=').slice(1).join('=')));
  } catch { /* fall through to localStorage */ }
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

const writeStore = (key, payload) => {
  const encoded = encodeURIComponent(JSON.stringify(payload));
  try {
    document.cookie = `${key}=${encoded}; max-age=${MAX_AGE_DAYS * 24 * 60 * 60}; path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  } catch { /* ignore */ }
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch { /* ignore */ }
};

/** Last-touch attribution (what most reports want). */
export const getStoredUtm = () => readStore(UTM_COOKIE);

/** First-touch attribution - the campaign that originally found them. */
export const getFirstTouchUtm = () => readStore(UTM_FIRST_COOKIE);

/** Both copies, ready to send with a signup request. */
export const getAttribution = () => ({
  last_touch: getStoredUtm(),
  first_touch: getFirstTouchUtm(),
});

/** UTM params present in the current URL right now (not storage). */
export const readUrlUtm = (search = window.location.search) => {
  let params;
  try { params = new URLSearchParams(search || ''); } catch { return null; }
  const found = {};
  UTM_KEYS.forEach((k) => {
    const v = clean(params.get(k));
    if (v) found[k] = v;
  });
  return Object.keys(found).length ? found : null;
};

/** Appends the live campaign tags to an internal path so they survive the
 *  click into /login or /subscribe even when cookies/localStorage are blocked.
 *  Backup only - storage is still the primary carrier. */
export const appendUtm = (path) => {
  const source = readUrlUtm() || (() => {
    const stored = getStoredUtm();
    if (!stored) return null;
    const out = {};
    UTM_KEYS.forEach((k) => { if (stored[k]) out[k] = stored[k]; });
    return Object.keys(out).length ? out : null;
  })();
  if (!source) return path;
  const [base, existing = ''] = String(path).split('?');
  const p = new URLSearchParams(existing);
  Object.entries(source).forEach(([k, v]) => { if (!p.get(k)) p.set(k, v); });
  return `${base}?${p.toString()}`;
};

/** Captures UTM params from `search` when any are present. Returns the stored
 *  last-touch payload, or null when there was nothing to capture. */
export const captureUtm = (search = window.location.search, pathname = window.location.pathname) => {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    return null;
  }

  const found = {};
  UTM_KEYS.forEach((key) => {
    const val = clean(params.get(key));
    if (val) found[key] = val;
  });
  if (Object.keys(found).length === 0) return null;

  const payload = {
    utm_source: found.utm_source || null,
    utm_medium: found.utm_medium || null,
    utm_campaign: found.utm_campaign || null,
    utm_content: found.utm_content || null,
    utm_term: found.utm_term || null,
    landing_page: clean(`${pathname || '/'}${search || ''}`) || '/',
    landing_url: clean(window.location.href),
    captured_at: new Date().toISOString(),
  };

  writeStore(UTM_COOKIE, payload);
  if (!getFirstTouchUtm()) writeStore(UTM_FIRST_COOKIE, payload);

  return payload;
};
